import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import PersonaBadge from '../common/PersonaBadge';
import { PersonaMarkdown } from '../personas/PersonaMarkdown';
import { gymnasiumService } from '../../services/gymnasiumService';
import { Match, EloRating } from '../../types/gymnasium';
import api from '../../services/api';
import { FiSend, FiCheck, FiX, FiMinus, FiSkipForward, FiCpu, FiPlus, FiSearch, FiClock, FiPlay, FiRefreshCw, FiZap, FiTarget, FiArrowLeft } from 'react-icons/fi';
import GymSubHeader from './GymSubHeader';

interface AutoJudgeResult {
  winner: string;
  winner_label: string;
  analysis: Record<string, { strengths: string[]; weaknesses: string[] }>;
  summary: string;
}

const Arena: React.FC = () => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [personas, setPersonas] = useState<any[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [prompt, setPrompt] = useState('');
  const [category, setCategory] = useState('');
  const [match, setMatch] = useState<Match | null>(null);
  const [sending, setSending] = useState(false);
  const [voting, setVoting] = useState(false);
  const [eloDelta, setEloDelta] = useState<Record<string, number>>({});
  const [rankings, setRankings] = useState<Record<string, EloRating>>({});
  const [mode, setMode] = useState<'single' | 'series'>('single');
  const [seriesPrompts, setSeriesPrompts] = useState<{ prompt: string; category?: string; probe_source?: any }[]>([]);
  const [seriesResults, setSeriesResults] = useState<any[] | null>(null);
  const seriesBottomRef = useRef<HTMLDivElement>(null);
  const [runningSeries, setRunningSeries] = useState(false);
  const [matchHistory, setMatchHistory] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [reviewMatch, setReviewMatch] = useState<Match | null>(null);
  const [autoJudge, setAutoJudge] = useState<AutoJudgeResult | null>(null);
  const [judging, setJudging] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [openSlot, setOpenSlot] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; icon: string; color: string } | null>(null);
  const [arenaProbes, setArenaProbes] = useState<any>(null); // {pid: {probes, capabilities}}
  const [gauntletScenarios, setGauntletScenarios] = useState<any[]>([]); // flattened scenarios from all gauntlets
  const [selectedProbeSource, setSelectedProbeSource] = useState<any>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [resp, rk, gs] = await Promise.all([
          api.get('/personas?limit=500'),
          gymnasiumService.getRankings(),
          gymnasiumService.listGauntlets(),
        ]);
        const data = resp.data?.personas || resp.data || [];
        setPersonas(data);
        const ratingMap: Record<string, EloRating> = {};
        for (const r of (rk.ratings || [])) ratingMap[r.persona_id] = r;
        setRankings(ratingMap);

        // Load full gauntlet details to get scenarios
        const allScenarios: any[] = [];
        for (const g of gs.slice(0, 10)) {
          try {
            const detail = await gymnasiumService.getGauntlet(g.id);
            if (detail?.scenarios) {
              for (const s of detail.scenarios) {
                allScenarios.push({ ...s, gauntlet_name: g.name, gauntlet_id: g.id });
              }
            }
          } catch {} // skip failed gauntlets
        }
        setGauntletScenarios(allScenarios);
        // Load match history
        const mh = await gymnasiumService.listMatches();
        setMatchHistory(mh);
      } catch (err) {
        console.error('Failed to load arena data:', err);
      }
    };
    load();
  }, []);

  // Fetch probes when 2 personas are selected
  useEffect(() => {
    if (selected.length === 2) {
      gymnasiumService.getArenaProbes(selected)
        .then(setArenaProbes)
        .catch(console.error);
    } else {
      setArenaProbes(null);
      setSelectedProbeSource(null);
    }
  }, [selected]);

  const showToast = (message: string, icon: string, color: string) => {
    setToast({ message, icon, color });
    setTimeout(() => setToast(null), 3000);
  };

  const toggleSelect = (id: string) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : prev.length < 2 ? [...prev, id] : prev
    );
  };

  const selectProbe = (probe: any, personaName: string) => {
    setPrompt(probe.input_prompt);
    setCategory(probe.focus_area || '');
    setSelectedProbeSource({
      probe_id: probe.id,
      label: probe.label,
      focus_area: probe.focus_area,
      expected_criteria: probe.expected_criteria,
      source_persona: personaName,
    });
  };

  const selectGauntletScenario = (scenario: any) => {
    setPrompt(scenario.prompt);
    setCategory(scenario.category || '');
    setSelectedProbeSource({
      probe_id: scenario.id,
      label: scenario.label,
      focus_area: scenario.category,
      expected_criteria: (scenario.criteria || []).join('\n'),
      source_persona: scenario.gauntlet_name?.replace(/_/g, ' '),
    });
  };

  const handleSend = async () => {
    if (selected.length !== 2 || !prompt.trim()) return;
    setSending(true);
    setMatch(null);
    setEloDelta({});
    setAutoJudge(null);
    try {
      const result = await gymnasiumService.createMatch(selected, prompt, category || undefined, selectedProbeSource || undefined);
      setMatch(result);
      showToast('Match started — responses incoming', '\u2694\uFE0F', 'from-amber-500 to-orange-500');
    } catch (err) {
      console.error('Match failed:', err);
      showToast('Match failed — check console', '\u274C', 'from-red-500 to-red-600');
    } finally {
      setSending(false);
    }
  };

  const handleAutoJudge = async () => {
    if (!match) return;
    setJudging(true);
    try {
      const result = await gymnasiumService.autoJudgeMatch(match.id);
      setAutoJudge(result);
      showToast('Judge has rendered a verdict', '\u2696\uFE0F', 'from-violet-500 to-purple-500');
    } catch (err) {
      console.error('Auto-judge failed:', err);
      showToast('Auto-judge failed', '\u274C', 'from-red-500 to-red-600');
    } finally {
      setJudging(false);
    }
  };

  // Load persisted auto-judge when match loads (e.g. after refresh)
  React.useEffect(() => {
    if (match && (match as any).auto_judge && !autoJudge) {
      setAutoJudge((match as any).auto_judge);
    }
  }, [match]);

  const handleVote = async (winner: string) => {
    if (!match) return;
    setVoting(true);
    try {
      const before: Record<string, number> = {};
      for (const c of match.contestants) before[c.persona_id] = rankings[c.persona_id]?.elo || 1500;

      const result = await gymnasiumService.recordVote(match.id, winner);
      setMatch(result);

      const rk = await gymnasiumService.getRankings();
      const deltas: Record<string, number> = {};
      const newRatings: Record<string, EloRating> = {};
      for (const r of (rk.ratings || [])) {
        newRatings[r.persona_id] = r;
        if (before[r.persona_id] != null) deltas[r.persona_id] = r.elo - before[r.persona_id];
      }
      setRankings(newRatings);
      setEloDelta(deltas);
      const winLabel = winner === 'tie' ? 'Tie declared' : winner === 'void' ? 'Match voided' : winner === 'skip' ? 'Match skipped' : 'Winner declared';
      showToast(`${winLabel} — Elo updated`, '\uD83C\uDFC6', 'from-green-500 to-emerald-500');
      // Refresh match history
      gymnasiumService.listMatches().then(setMatchHistory).catch(() => {});
    } catch (err) {
      console.error('Vote failed:', err);
    } finally {
      setVoting(false);
    }
  };

  const handleAcceptVerdict = async () => {
    if (!autoJudge || !match) return;
    setVoting(true);
    try {
      const before: Record<string, number> = {};
      for (const c of match.contestants) before[c.persona_id] = rankings[c.persona_id]?.elo || 1500;

      // Pass notes indicating auto-judge acceptance
      const result = await gymnasiumService.recordVote(match.id, autoJudge.winner, `Auto-judge accepted: ${autoJudge.summary?.slice(0, 200) || ''}`);
      setMatch(result);

      const rk = await gymnasiumService.getRankings();
      const deltas: Record<string, number> = {};
      const newRatings: Record<string, EloRating> = {};
      for (const r of (rk.ratings || [])) {
        newRatings[r.persona_id] = r;
        if (before[r.persona_id] != null) deltas[r.persona_id] = r.elo - before[r.persona_id];
      }
      setRankings(newRatings);
      setEloDelta(deltas);
      showToast('Verdict accepted — Elo updated', '\u2696\uFE0F', 'from-green-500 to-emerald-500');
    } catch (err) {
      console.error('Accept verdict failed:', err);
    } finally {
      setVoting(false);
    }
  };

  const addSeriesPrompt = (promptText: string, cat?: string, probeSource?: any) => {
    setSeriesPrompts(prev => [...prev, { prompt: promptText, category: cat, probe_source: probeSource }]);
  };

  const removeSeriesPrompt = (index: number) => {
    setSeriesPrompts(prev => prev.filter((_, i) => i !== index));
  };

  const [seriesCurrentIdx, setSeriesCurrentIdx] = useState(-1);
  const [seriesCurrentMatch, setSeriesCurrentMatch] = useState<Match | null>(null);
  const [seriesCurrentJudge, setSeriesCurrentJudge] = useState<AutoJudgeResult | null>(null);
  const [seriesPhase, setSeriesPhase] = useState<'idle' | 'responding' | 'judging' | 'voting' | 'done'>('idle');

  // Auto-scroll to bottom when new results arrive
  useEffect(() => {
    if (seriesResults && seriesResults.length > 0 && seriesBottomRef.current) {
      seriesBottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [seriesResults?.length]);

  const handleRunSeries = async () => {
    if (selected.length !== 2 || seriesPrompts.length === 0) return;
    setRunningSeries(true);
    setSeriesResults([]);
    setSeriesCurrentIdx(0);
    showToast(`Series starting — ${seriesPrompts.length} matches`, '\uD83C\uDFAF', 'from-violet-500 to-purple-500');

    const results: any[] = [];

    for (let i = 0; i < seriesPrompts.length; i++) {
      const sp = seriesPrompts[i];
      setSeriesCurrentIdx(i);
      setSeriesCurrentMatch(null);
      setSeriesCurrentJudge(null);

      try {
        // Phase 1: Create match (get responses)
        setSeriesPhase('responding');
        const matchResult = await gymnasiumService.createMatch(
          selected, sp.prompt, sp.category || undefined, sp.probe_source || undefined
        );
        setSeriesCurrentMatch(matchResult);

        // Phase 2: Auto-judge
        setSeriesPhase('judging');
        const judgeResult = await gymnasiumService.autoJudgeMatch(matchResult.id);
        setSeriesCurrentJudge(judgeResult);

        // Phase 3: Auto-accept verdict
        setSeriesPhase('voting');
        const winner = judgeResult.winner || 'tie';
        await gymnasiumService.recordVote(
          matchResult.id, winner,
          `Series auto-accept: ${judgeResult.summary?.slice(0, 200) || ''}`
        );

        const entry = {
          match_id: matchResult.id,
          prompt_preview: sp.prompt.slice(0, 100),
          winner,
          winner_name: matchResult.contestants.find((c: any) => c.persona_id === winner)?.persona_name || winner,
          summary: judgeResult.summary || '',
        };
        results.push(entry);
        setSeriesResults([...results]);
      } catch (err) {
        console.error(`Series match ${i + 1} failed:`, err);
        results.push({
          match_id: null,
          prompt_preview: sp.prompt.slice(0, 100),
          winner: null,
          winner_name: null,
          summary: `Error: ${err}`,
        });
        setSeriesResults([...results]);
      }
    }

    setSeriesPhase('done');
    setSeriesCurrentMatch(null);
    setSeriesCurrentJudge(null);
    setSeriesCurrentIdx(-1);
    setRunningSeries(false);

    // Refresh rankings + history
    try {
      const [rk, mh] = await Promise.all([gymnasiumService.getRankings(), gymnasiumService.listMatches()]);
      const newRatings: Record<string, EloRating> = {};
      for (const r of (rk.ratings || [])) newRatings[r.persona_id] = r;
      setRankings(newRatings);
      setMatchHistory(mh);
    } catch {}

    showToast(`Series complete — ${results.length} matches judged`, '\uD83C\uDFC6', 'from-green-500 to-emerald-500');
  };

  const handleReviewMatch = async (matchId: string) => {
    try {
      const m = await gymnasiumService.getMatch(matchId);
      setReviewMatch(m);
    } catch (err) {
      console.error('Failed to load match:', err);
    }
  };

  const handleRerun = () => {
    // Keep personas + prompts, clear results
    setMatch(null);
    setEloDelta({});
    setAutoJudge(null);
    setReviewMatch(null);
    // For single: re-send same prompt
    if (mode === 'single' && prompt.trim()) {
      handleSend();
    }
    // For series: re-run same series
    if (mode === 'series' && seriesPrompts.length > 0) {
      setSeriesResults(null);
      // Slight delay so state clears first
      setTimeout(() => handleRunSeries(), 100);
    }
  };

  const handleRerunFrom = (m: Match | any) => {
    // Load a match's config and re-run it
    const pids = m.contestants?.map((c: any) => c.persona_id) || [];
    if (pids.length === 2) {
      setSelected(pids);
      setPrompt(m.prompt || '');
      setCategory(m.prompt_category || '');
      setSelectedProbeSource(m.probe_context || null);
      setMatch(null);
      setEloDelta({});
      setAutoJudge(null);
      setReviewMatch(null);
      setSeriesResults(null);
      setMode('single');
    }
  };

  const handleNewMatch = () => {
    setMatch(null);
    setPrompt('');
    setCategory('');
    setEloDelta({});
    setAutoJudge(null);
    setSelected([]);
    setSelectedProbeSource(null);
    setArenaProbes(null);
    setSeriesResults(null);
    setSeriesPrompts([]);
    setReviewMatch(null);
  };

  /** Pick a random persona that's not already selected */
  const randomPick = (exclude: string[]): string | null => {
    const available = personas.filter((p: any) => !exclude.includes(p.id));
    if (available.length === 0) return null;
    return available[Math.floor(Math.random() * available.length)].id;
  };

  const autoPickSlot = (slot: number) => {
    const otherId = selected[slot === 0 ? 1 : 0] || '';
    const pick = randomPick([otherId, ...selected]);
    if (pick) {
      setSelected(prev => {
        const next = [...prev];
        next[slot] = pick;
        return next;
      });
    }
  };

  const autoPickBoth = () => {
    const first = randomPick([]);
    if (!first) return;
    const second = randomPick([first]);
    if (!second) return;
    setSelected([first, second]);
  };

  const sectionClass = `rounded-lg shadow-lg border-l-4 p-5 ${
    darkMode ? 'bg-gray-800 border-amber-600' : 'bg-white border-amber-500 shadow-sm'
  }`;

  return (
    <div className="space-y-4">
      <GymSubHeader
        icon={<FiZap className="w-4 h-4" />}
        title="Arena"
        subtitle="Head-to-head persona comparison"
        accentFrom="from-amber-400"
        accentTo="to-red-400"
      />

      {/* Persona Picker — Two Arena Cards with VS */}
      {!match && (
        <>
          <div className="flex items-center justify-center gap-6">
              {[0, 1].map(slot => {
                const pid = selected[slot];
                const persona = pid ? personas.find((p: any) => p.id === pid) : null;
                const elo = pid ? rankings[pid]?.elo : null;
                const probeCount = arenaProbes?.[pid]?.probes?.length || 0;
                const capCount = arenaProbes?.[pid]?.capabilities ? Object.keys(arenaProbes[pid].capabilities).length : 0;
                const gradFrom = slot === 0 ? 'from-blue-600' : 'from-red-600';
                const gradTo = slot === 0 ? 'to-indigo-600' : 'to-orange-600';
                const glowColor = slot === 0 ? 'rgba(59,130,246,0.25)' : 'rgba(239,68,68,0.25)';
                const borderActive = slot === 0 ? 'border-blue-500/60' : 'border-red-500/60';
                const borderDashed = slot === 0 ? 'border-blue-500/30' : 'border-red-500/30';

                return (
                  <React.Fragment key={slot}>
                    <div className="w-[280px] relative group" style={{ minHeight: 220 }}>
                      {persona ? (
                        <div
                          className={`rounded-2xl border-2 ${borderActive} p-6 text-center h-full flex flex-col items-center justify-center transition-all duration-300 ${
                            darkMode
                              ? 'bg-gradient-to-br from-gray-800/90 to-gray-900/90'
                              : 'bg-gradient-to-br from-white to-gray-50'
                          }`}
                          style={{ boxShadow: `0 0 30px ${glowColor}`, minHeight: 220 }}
                        >
                          {/* Role label */}
                          <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-0.5 rounded-full text-[10px] font-bold tracking-widest text-white bg-gradient-to-r ${gradFrom} ${gradTo} shadow-lg`}>
                            {slot === 0 ? 'CHALLENGER' : 'DEFENDER'}
                          </div>

                          {/* Remove */}
                          <button
                            onClick={() => setSelected(prev => prev.filter((_, i) => i !== slot))}
                            className={`absolute top-3 right-3 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                          >
                            <FiX className="w-3.5 h-3.5" />
                          </button>

                          <PersonaBadge personaId={persona.id} personaName={persona.name} size="lg" clickable={false} />
                          <h3 className={`text-base font-bold mt-3 mb-1 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                            {persona.name?.replace(/_/g, ' ')}
                          </h3>
                          <span className={`text-sm px-3 py-1 rounded-full font-bold mb-2 ${
                            darkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {elo || 1500} Elo
                          </span>
                          <div className="flex justify-center gap-3 mt-1">
                            {probeCount > 0 && <span className={`text-[10px] ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>{probeCount} probes</span>}
                            {capCount > 0 && <span className={`text-[10px] ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>{capCount} caps</span>}
                          </div>
                          {(persona.tags || []).length > 0 && (
                            <div className="flex justify-center gap-1 mt-2 flex-wrap">
                              {persona.tags.slice(0, 3).map((t: string) => (
                                <span key={t} className={`text-[9px] px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700/60 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <button
                            onClick={() => setOpenSlot(slot)}
                            className={`w-full rounded-2xl border-2 border-dashed ${borderDashed} text-center flex flex-col items-center justify-center transition-all duration-300 hover:scale-[1.02] ${
                              darkMode
                                ? 'bg-gray-800/20 hover:bg-gray-800/40 hover:border-opacity-60'
                                : 'bg-gray-50/50 hover:bg-gray-100 hover:border-opacity-60'
                            }`}
                            style={{ minHeight: 220 }}
                          >
                            <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-0.5 rounded-full text-[10px] font-bold tracking-widest ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                              {slot === 0 ? 'CHALLENGER' : 'DEFENDER'}
                            </div>
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 border-2 border-dashed transition-colors ${
                              slot === 0
                                ? darkMode ? 'border-blue-500/30 text-blue-400/50' : 'border-blue-300 text-blue-400'
                                : darkMode ? 'border-red-500/30 text-red-400/50' : 'border-red-300 text-red-400'
                            }`}>
                              <FiPlus className="w-8 h-8" />
                            </div>
                            <p className={`text-sm font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Select Persona
                            </p>
                          </button>
                          <button
                            onClick={() => autoPickSlot(slot)}
                            className={`mt-2 px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                              darkMode ? 'bg-gray-700/60 text-amber-400 hover:bg-gray-700' : 'bg-gray-100 text-amber-600 hover:bg-gray-200'
                            }`}
                          >
                            <FiZap className="w-3 h-3 inline mr-1" />Auto-Choose
                          </button>
                        </div>
                      )}
                    </div>

                    {/* VS badge between the two cards */}
                    {slot === 0 && (
                      <div className="flex flex-col items-center gap-1.5 shrink-0">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center font-black text-xl tracking-tight transition-all duration-500 ${
                          selected.length === 2
                            ? 'bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white shadow-[0_0_25px_rgba(245,158,11,0.4)] scale-110'
                            : darkMode ? 'bg-gray-700/40 text-gray-600' : 'bg-gray-200/60 text-gray-400'
                        }`}>
                          VS
                        </div>
                        {selected.length === 2 ? (
                          <div className="flex flex-col items-center gap-2">
                            <span className="text-[9px] font-bold tracking-wider text-amber-400 animate-pulse">READY</span>
                            <button
                              onClick={autoPickBoth}
                              className="px-4 py-1.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 hover:from-amber-400 hover:via-orange-400 hover:to-red-400 shadow-lg hover:shadow-[0_0_20px_rgba(245,158,11,0.5)] transition-all duration-300 hover:scale-110 active:scale-95 animate-[shimmer_2s_ease-in-out_infinite]"
                            >
                              <FiRefreshCw className="w-3.5 h-3.5 inline mr-1.5" />Re-Roll
                            </button>
                            <style>{`@keyframes shimmer{0%,100%{box-shadow:0 0 8px rgba(245,158,11,0.3)}50%{box-shadow:0 0 20px rgba(245,158,11,0.6),0 0 40px rgba(239,68,68,0.2)}}`}</style>
                          </div>
                        ) : (
                          <button
                            onClick={autoPickBoth}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 shadow-md hover:shadow-lg`}
                          >
                            <FiZap className="w-3 h-3 inline mr-1" />Randomize
                          </button>
                        )}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Persona picker modal */}
            {openSlot !== null && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setOpenSlot(null)}>
                <div
                  className={`w-[480px] max-h-[70vh] rounded-2xl shadow-2xl overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
                  onClick={e => e.stopPropagation()}
                >
                  <div className={`px-5 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <h3 className={`text-lg font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                      Select {openSlot === 0 ? 'Challenger' : 'Defender'}
                    </h3>
                    <div className="relative mt-2">
                      <FiSearch className={`absolute left-3 top-2.5 w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      <input
                        type="text"
                        placeholder="Search personas..."
                        autoFocus
                        onChange={e => setPickerSearch(e.target.value)}
                        value={pickerSearch}
                        className={`w-full pl-10 pr-4 py-2 rounded-lg text-sm ${
                          darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-800'
                        } border focus:outline-none focus:ring-2 focus:ring-amber-500/30`}
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto max-h-[50vh]">
                    {personas
                      .filter((p: any) => !selected.includes(p.id))
                      .filter((p: any) => !pickerSearch || p.name?.toLowerCase().includes(pickerSearch.toLowerCase()) || (p.tags || []).some((t: string) => t.toLowerCase().includes(pickerSearch.toLowerCase())))
                      .map((p: any) => {
                        const pElo = rankings[p.id]?.elo;
                        return (
                          <button
                            key={p.id}
                            onClick={() => {
                              setSelected(prev => { const next = [...prev]; next[openSlot!] = p.id; return next.filter(Boolean); });
                              setOpenSlot(null);
                              setPickerSearch('');
                            }}
                            className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors border-b ${
                              darkMode ? 'hover:bg-gray-700/60 border-gray-700/30' : 'hover:bg-gray-50 border-gray-100'
                            }`}
                          >
                            <PersonaBadge personaId={p.id} personaName={p.name} size="md" clickable={false} />
                            <div className="flex-1 min-w-0">
                              <span className={`text-sm font-medium block truncate ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                                {p.name?.replace(/_/g, ' ')}
                              </span>
                              {(p.tags || []).length > 0 && (
                                <div className="flex gap-1 mt-0.5">
                                  {p.tags.slice(0, 3).map((t: string) => (
                                    <span key={t} className={`text-[9px] px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{t}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <span className={`text-sm font-bold ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                              {pElo || 1500}
                            </span>
                          </button>
                        );
                      })}
                  </div>
                </div>
              </div>
            )}
          </>
      )}

      {/* Mode Toggle + Prompt Input */}
      {!match && !seriesResults && !reviewMatch && (
        <div className={sectionClass}>
          {/* Mode tabs */}
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => { setMode('single'); setSeriesPrompts([]); }}
              className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                mode === 'single'
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                  : darkMode ? 'bg-gray-700/40 text-gray-400' : 'bg-gray-100 text-gray-500'
              }`}
            >
              Single Match
            </button>
            <button
              onClick={() => { setMode('series'); setSelectedProbeSource(null); }}
              className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                mode === 'series'
                  ? 'bg-violet-500/20 text-violet-300 border border-violet-500/40'
                  : darkMode ? 'bg-gray-700/40 text-gray-400' : 'bg-gray-100 text-gray-500'
              }`}
            >
              Series ({seriesPrompts.length > 0 ? `${seriesPrompts.length} queued` : 'build queue'})
            </button>
            <div className="flex-1" />
          </div>

          {/* Shared probe/gauntlet picker */}
          {(() => {
            const probeItems = arenaProbes
              ? Object.values(arenaProbes).flatMap((pctx: any) =>
                  (pctx.probes || []).map((probe: any) => ({ ...probe, source: pctx.persona_name, type: 'probe' }))
                )
              : [];
            const scenarioItems = gauntletScenarios.map(s => ({
              id: s.id, label: s.label, focus_area: s.category,
              input_prompt: s.prompt, expected_criteria: (s.criteria || []).join('\n'),
              source: s.gauntlet_name?.replace(/_/g, ' '), type: 'gauntlet', _scenario: s,
            }));
            const allItems = [...probeItems, ...scenarioItems];
            if (allItems.length === 0) return null;
            return (
              <div className={`mb-3 rounded-lg border p-3 ${darkMode ? 'border-gray-700/40 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`}>
                <p className={`text-[10px] font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                  {mode === 'single' ? 'Click to use as prompt' : 'Click items or toggle entire gauntlets'}
                </p>

                {/* Series mode: gauntlet-level toggle buttons */}
                {mode === 'series' && (() => {
                  const gauntletGroups = new Map<string, { name: string; scenarios: any[] }>();
                  scenarioItems.forEach(s => {
                    const gid = s._scenario?.gauntlet_id || 'unknown';
                    const gname = s.source || 'Unknown';
                    if (!gauntletGroups.has(gid)) gauntletGroups.set(gid, { name: gname, scenarios: [] });
                    gauntletGroups.get(gid)!.scenarios.push(s);
                  });
                  if (gauntletGroups.size === 0) return null;
                  return (
                    <div className="flex gap-2 flex-wrap mb-2">
                      {Array.from(gauntletGroups.entries()).map(([gid, g]) => {
                        const allIn = g.scenarios.every(s => seriesPrompts.some(sp => sp.prompt === (s.input_prompt || s._scenario?.prompt || '')));
                        const someIn = !allIn && g.scenarios.some(s => seriesPrompts.some(sp => sp.prompt === (s.input_prompt || s._scenario?.prompt || '')));
                        return (
                          <button
                            key={gid}
                            onClick={() => {
                              if (allIn) {
                                // Remove all scenarios from this gauntlet
                                const prompts = new Set(g.scenarios.map(s => s.input_prompt || s._scenario?.prompt || ''));
                                setSeriesPrompts(prev => prev.filter(sp => !prompts.has(sp.prompt)));
                              } else {
                                // Add all scenarios from this gauntlet that aren't already queued
                                g.scenarios.forEach(s => {
                                  const p = s.input_prompt || s._scenario?.prompt || '';
                                  if (!seriesPrompts.some(sp => sp.prompt === p)) {
                                    addSeriesPrompt(p, s.focus_area || s._scenario?.category, {
                                      probe_id: s.id, label: s.label, focus_area: s.focus_area,
                                      expected_criteria: s.expected_criteria, source_persona: s.source
                                    });
                                  }
                                });
                                showToast(`Added ${g.scenarios.length} scenarios from ${g.name}`, '\uD83C\uDFAF', 'from-violet-500 to-purple-500');
                              }
                            }}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                              allIn
                                ? 'bg-violet-500/20 text-violet-300 border border-violet-500/40'
                                : someIn
                                  ? 'bg-violet-500/10 text-violet-300/70 border border-violet-500/20 border-dashed'
                                  : darkMode ? 'bg-gray-700/50 text-gray-300 hover:bg-gray-700 border border-gray-700/30' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                            }`}
                          >
                            <FiTarget className="w-3 h-3" />
                            {g.name} ({g.scenarios.length})
                            {allIn && <FiCheck className="w-3 h-3 text-violet-400" />}
                          </button>
                        );
                      })}
                    </div>
                  );
                })()}

                <div className="flex gap-2 flex-wrap max-h-[120px] overflow-y-auto">
                  {allItems.map((item: any, idx: number) => {
                    const inQueue = mode === 'series' && seriesPrompts.some(sp => sp.prompt === (item.input_prompt || item._scenario?.prompt || ''));
                    return (
                    <button
                      key={`${item.type}-${item.id}-${idx}`}
                      onClick={() => {
                        if (mode === 'single') {
                          const p = item.input_prompt || item._scenario?.prompt || '';
                          setPrompt(p);
                          setCategory(item.focus_area || item._scenario?.category || '');
                          setSelectedProbeSource({ probe_id: `${item.type}-${idx}`, label: item.label, focus_area: item.focus_area, expected_criteria: item.expected_criteria, source_persona: item.source });
                        } else if (inQueue) {
                          // Series: toggle OFF — remove from queue
                          const p = item.input_prompt || item._scenario?.prompt || '';
                          setSeriesPrompts(prev => prev.filter(sp => sp.prompt !== p));
                        } else {
                          // Series: toggle ON — add to queue
                          addSeriesPrompt(
                            item.input_prompt || item._scenario?.prompt || '',
                            item.focus_area || item._scenario?.category,
                            { probe_id: item.id, label: item.label, focus_area: item.focus_area, expected_criteria: item.expected_criteria, source_persona: item.source }
                          );
                        }
                      }}
                      className={`text-left px-2.5 py-1.5 rounded-lg text-xs transition-all ${
                        inQueue
                          ? 'bg-violet-500/20 text-violet-300 border border-violet-500/40'
                          : mode === 'single' && selectedProbeSource?.probe_id === `${item.type}-${idx}`
                            ? 'bg-amber-500/20 border-amber-500/50 text-amber-300 border'
                            : darkMode ? 'bg-gray-700/40 hover:bg-gray-700 text-gray-300 border border-gray-700/30' : 'bg-white hover:bg-gray-100 text-gray-600 border border-gray-200'
                      }`}
                    >
                      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${item.type === 'probe' ? 'bg-violet-400' : 'bg-amber-400'}`} />
                      <span className="font-medium">{item.label?.replace(/_/g, ' ')}</span>
                      <span className={`ml-1.5 text-[9px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {item.focus_area}
                      </span>
                      {inQueue && <FiCheck className="ml-1 w-3 h-3 text-violet-400 inline" />}
                    </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Single match prompt */}
          {mode === 'single' && (
            <>
              <textarea
                value={prompt}
                onChange={e => { setPrompt(e.target.value); if (selectedProbeSource) setSelectedProbeSource(null); }}
                placeholder="Enter the prompt both personas will respond to..."
                className={`w-full px-4 py-3 rounded-lg text-sm h-24 ${
                  darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-800'
                } border focus:outline-none focus:ring-2 focus:ring-amber-500/30`}
              />
              <div className="flex items-center justify-between mt-3">
                <input
                  value={category}
                  onChange={e => setCategory(e.target.value)}
                  placeholder="Category (optional)"
                  className={`px-3 py-2 rounded-lg text-sm w-48 ${
                    darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200'
                  } border`}
                />
                <button
                  onClick={handleSend}
                  disabled={selected.length !== 2 || !prompt.trim() || sending}
                  className="px-6 py-2 text-sm rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 transition-all flex items-center gap-2"
                >
                  <FiSend className="w-4 h-4" />
                  {sending ? 'Matching...' : 'Begin Match'}
                </button>
              </div>
            </>
          )}

          {/* Series mode */}
          {mode === 'series' && (
            <>
              {/* Add custom prompt */}
              <div className="flex gap-2 mb-3">
                <textarea
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  placeholder="Type a prompt and click + to add to series, or click probes/gauntlets above..."
                  className={`flex-1 px-3 py-2 rounded-lg text-sm h-16 ${
                    darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200 text-gray-800'
                  } border focus:outline-none focus:ring-2 focus:ring-violet-500/30`}
                />
                <button
                  onClick={() => { if (prompt.trim()) { addSeriesPrompt(prompt, category || undefined); setPrompt(''); } }}
                  disabled={!prompt.trim()}
                  className={`px-3 self-end rounded-lg ${darkMode ? 'bg-violet-600 hover:bg-violet-500 disabled:bg-gray-700' : 'bg-violet-500 hover:bg-violet-600 disabled:bg-gray-200'} text-white disabled:opacity-50 h-10`}
                >
                  <FiPlus className="w-4 h-4" />
                </button>
              </div>

              {/* Series queue */}
              {seriesPrompts.length > 0 && (
                <div className={`rounded-lg border p-3 mb-3 ${darkMode ? 'border-gray-700/40 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`}>
                  <p className={`text-[10px] font-semibold uppercase tracking-wider mb-2 ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                    Series Queue ({seriesPrompts.length} {seriesPrompts.length === 1 ? 'match' : 'matches'})
                  </p>
                  <div className="space-y-1.5 max-h-[150px] overflow-y-auto">
                    {seriesPrompts.map((sp, i) => (
                      <div key={i} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs ${darkMode ? 'bg-gray-700/30' : 'bg-white'}`}>
                        <span className={`font-bold w-5 text-center ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>{i + 1}</span>
                        <span className={`flex-1 truncate ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{sp.prompt.slice(0, 80)}{sp.prompt.length > 80 ? '...' : ''}</span>
                        {sp.category && <span className={`text-[9px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{sp.category}</span>}
                        <button onClick={() => removeSeriesPrompt(i)} className="text-red-400 hover:text-red-300 shrink-0">
                          <FiX className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={handleRunSeries}
                disabled={selected.length !== 2 || seriesPrompts.length === 0 || runningSeries}
                className="w-full py-3 text-sm rounded-lg bg-gradient-to-r from-violet-500 to-purple-500 text-white hover:from-violet-600 hover:to-purple-600 disabled:opacity-50 transition-all flex items-center justify-center gap-2 font-semibold"
              >
                {runningSeries ? (
                  <><span className="animate-spin">&#9881;</span> Running Series...</>
                ) : (
                  <><FiPlay className="w-4 h-4" /> Run Series ({seriesPrompts.length} matches, auto-judged)</>
                )}
              </button>
            </>
          )}
        </div>
      )}

      {/* Series Live View + Results */}
      {seriesResults && !reviewMatch && (
        <div className="space-y-4">
          {/* Live match — fixed height container, content scrolls inside */}
          {runningSeries && seriesCurrentIdx >= 0 && (
            <div className={`${sectionClass} border-violet-500/30`}>
              <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                    Match {seriesCurrentIdx + 1} of {seriesPrompts.length}
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full animate-pulse ${
                    seriesPhase === 'responding' ? 'bg-amber-500/20 text-amber-300' :
                    seriesPhase === 'judging' ? 'bg-violet-500/20 text-violet-300' :
                    seriesPhase === 'voting' ? 'bg-green-500/20 text-green-300' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {seriesPhase === 'responding' ? 'Generating responses...' :
                     seriesPhase === 'judging' ? 'Auto-judging...' :
                     seriesPhase === 'voting' ? 'Recording verdict...' : ''}
                  </span>
                </div>
                {/* Progress bar */}
                <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {Math.round(((seriesCurrentIdx) / seriesPrompts.length) * 100)}%
                </span>
              </div>

              {/* Progress bar */}
              <div className={`h-1.5 rounded-full mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <div
                  className="h-1.5 rounded-full bg-gradient-to-r from-violet-500 to-purple-500 transition-all duration-500"
                  style={{ width: `${((seriesCurrentIdx + (seriesPhase === 'voting' ? 0.9 : seriesPhase === 'judging' ? 0.6 : 0.3)) / seriesPrompts.length) * 100}%` }}
                />
              </div>

              {/* Current prompt */}
              <div className={`mb-3 p-3 rounded-lg ${darkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <p className={`text-[10px] uppercase tracking-wider font-semibold mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Current Prompt</p>
                <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {seriesPrompts[seriesCurrentIdx]?.prompt.slice(0, 200)}{seriesPrompts[seriesCurrentIdx]?.prompt.length > 200 ? '...' : ''}
                </p>
              </div>

              {/* Live responses */}
              {seriesCurrentMatch && (
                <div className="grid grid-cols-2 gap-3">
                  {seriesCurrentMatch.contestants.map(c => (
                    <div key={c.persona_id} className={`p-3 rounded-lg ${darkMode ? 'bg-gray-800/30' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <PersonaBadge personaId={c.persona_id} personaName={c.persona_name} size="xs" clickable={false} />
                        <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {c.persona_name.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="max-h-[150px] overflow-y-auto">
                        <PersonaMarkdown content={c.response?.slice(0, 500) || ''} size="small" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Live judge result */}
              {seriesCurrentJudge && (
                <div className={`mt-3 p-3 rounded-lg border ${darkMode ? 'border-violet-500/20 bg-violet-900/10' : 'border-violet-200 bg-violet-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <FiCpu className="w-3 h-3 text-violet-400" />
                    <span className={`text-xs font-semibold ${darkMode ? 'text-violet-300' : 'text-violet-600'}`}>
                      Winner: {seriesCurrentMatch?.contestants.find((c: any) => c.persona_id === seriesCurrentJudge.winner)?.persona_name?.replace(/_/g, ' ') || seriesCurrentJudge.winner}
                    </span>
                  </div>
                  <PersonaMarkdown content={seriesCurrentJudge.summary || ''} size="small" />
                </div>
              )}
              </div>{/* end inner scroll */}
            </div>
          )}

          {/* Results scoreboard */}
          <div className={sectionClass}>
            <h2 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {runningSeries ? 'Results So Far' : 'Series Results'} ({seriesResults.length}{runningSeries ? ` / ${seriesPrompts.length}` : ''})
            </h2>

            {/* Win tally */}
            {seriesResults.length > 0 && selected.length === 2 && (
              <div className="flex items-center gap-3 mb-3">
                {selected.map(pid => {
                  const p = personas.find((pp: any) => pp.id === pid);
                  const wins = seriesResults.filter((r: any) => r.winner === pid).length;
                  const total = seriesResults.filter((r: any) => r.winner).length;
                  return (
                    <div key={pid} className="flex items-center gap-2 flex-1">
                      <PersonaBadge personaId={pid} personaName={p?.name || pid.slice(0, 8)} size="xs" clickable={false} />
                      <span className={`text-lg font-black ${darkMode ? 'text-amber-300' : 'text-amber-600'}`}>{wins}</span>
                      <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>wins</span>
                    </div>
                  );
                })}
                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {seriesResults.filter((r: any) => r.winner === 'tie').length} ties
                </span>
              </div>
            )}

            <div className="space-y-1.5">
              {seriesResults.map((r: any, i: number) => (
                <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg ${darkMode ? 'bg-gray-800/40' : 'bg-gray-50'}`}>
                  <span className={`font-bold w-5 text-center text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{i + 1}</span>
                  <span className={`text-xs flex-1 truncate ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{r.prompt_preview}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    r.winner === 'tie' ? 'bg-gray-600/30 text-gray-400' :
                    !r.winner ? 'bg-red-600/20 text-red-400' :
                    'bg-green-600/20 text-green-400'
                  }`}>
                    {r.winner === 'tie' ? 'Tie' : !r.winner ? 'Error' : (r.winner_name || '').replace(/_/g, ' ') || 'Winner'}
                  </span>
                  {r.match_id && (
                    <button
                      onClick={() => handleReviewMatch(r.match_id)}
                      className={`text-[10px] ${darkMode ? 'text-amber-400 hover:text-amber-300' : 'text-amber-600 hover:text-amber-500'}`}
                    >
                      Review
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div ref={seriesBottomRef} />
            {!runningSeries && seriesResults.length > 0 && (
              <div className="flex justify-center gap-3 mt-4">
                <button
                  onClick={handleRerun}
                  className={`px-4 py-2 text-sm rounded-lg flex items-center gap-2 ${darkMode ? 'bg-violet-600 hover:bg-violet-500' : 'bg-violet-500 hover:bg-violet-600'} text-white transition-all`}
                >
                  <FiRefreshCw className="w-4 h-4" /> Re-run Series
                </button>
                <button onClick={handleNewMatch} className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600">
                  New Series
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Match Review */}
      {reviewMatch && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <button onClick={() => setReviewMatch(null)} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
              <FiArrowLeft className="w-4 h-4" />
            </button>
            <h2 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Match Review</h2>
          </div>
          <div className={sectionClass}>
            <p className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Prompt</p>
            <PersonaMarkdown content={reviewMatch.prompt} size="small" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reviewMatch.contestants.map(c => {
              const isWinner = reviewMatch.verdict?.winner === c.persona_id;
              return (
                <div key={c.persona_id} className={`${sectionClass} ${isWinner ? 'ring-2 ring-green-500 shadow-[0_0_20px_rgba(34,197,94,0.2)]' : ''}`}>
                  {isWinner && <div className="text-center mb-2"><span className="px-3 py-0.5 rounded-full bg-green-500 text-white text-xs font-bold">WINNER</span></div>}
                  <div className="flex items-center gap-2 mb-3">
                    <PersonaBadge personaId={c.persona_id} personaName={c.persona_name} size="sm" clickable={false} />
                    <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{c.persona_name.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto">
                    <PersonaMarkdown content={c.response} size="small" />
                  </div>
                  {/* Show auto-judge analysis if available */}
                  {(reviewMatch as any).auto_judge?.analysis?.[c.persona_id] && (
                    <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-gray-700/40' : 'border-gray-200'}`}>
                      {(reviewMatch as any).auto_judge.analysis[c.persona_id].strengths?.map((s: string, i: number) => (
                        <div key={`s${i}`} className="flex items-start gap-1.5"><span className="text-green-400 text-xs mt-0.5">+</span><span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{s}</span></div>
                      ))}
                      {(reviewMatch as any).auto_judge.analysis[c.persona_id].weaknesses?.map((w: string, i: number) => (
                        <div key={`w${i}`} className="flex items-start gap-1.5"><span className="text-red-400 text-xs mt-0.5">-</span><span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{w}</span></div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {(reviewMatch as any).auto_judge?.summary && (
            <div className={sectionClass}>
              <p className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>Judge Analysis</p>
              <PersonaMarkdown content={(reviewMatch as any).auto_judge.summary} size="small" />
            </div>
          )}
          {/* Review actions */}
          <div className="flex justify-center gap-3">
            <button
              onClick={() => handleRerunFrom(reviewMatch)}
              className={`px-4 py-2 text-sm rounded-lg flex items-center gap-2 ${darkMode ? 'bg-violet-600 hover:bg-violet-500' : 'bg-violet-500 hover:bg-violet-600'} text-white transition-all`}
            >
              <FiRefreshCw className="w-4 h-4" /> Re-run This Match
            </button>
            <button
              onClick={() => setReviewMatch(null)}
              className={`px-4 py-2 text-sm rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
            >
              Back
            </button>
          </div>
        </div>
      )}

      {/* Match History Panel */}

      {/* Responses */}
      {match && (
        <>
          <div className={sectionClass}>
            <p className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Prompt</p>
            <PersonaMarkdown content={match.prompt} size="small" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {match.contestants.map(c => {
              const isWinner = match.verdict?.winner === c.persona_id;
              const isAutoWinner = autoJudge?.winner === c.persona_id && !match.verdict;
              const delta = eloDelta[c.persona_id];
              const analysis = autoJudge?.analysis?.[c.persona_id];

              return (
                <div
                  key={c.persona_id}
                  className={`${sectionClass} relative ${
                    isWinner ? 'ring-2 ring-green-500 shadow-[0_0_20px_rgba(34,197,94,0.2)]' :
                    isAutoWinner ? 'ring-2 ring-amber-500/50 shadow-[0_0_15px_rgba(245,158,11,0.15)]' : ''
                  }`}
                >
                  {isWinner && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-green-500 text-white text-xs font-bold">
                      WINNER
                    </div>
                  )}
                  {isAutoWinner && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center gap-1">
                      <FiCpu className="w-3 h-3" /> JUDGE PICK
                    </div>
                  )}

                  {/* Header */}
                  <div className="flex items-center gap-2 mb-3">
                    <PersonaBadge personaId={c.persona_id} personaName={c.persona_name} size="sm" clickable={false} />
                    <span className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      {c.persona_name.replace(/_/g, ' ')}
                    </span>
                    <div className="flex-1" />
                    {delta != null && (
                      <span className={`text-sm font-bold ${delta > 0 ? 'text-green-400' : delta < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                        {delta > 0 ? '+' : ''}{delta}
                      </span>
                    )}
                    <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{c.duration_ms}ms</span>
                  </div>

                  {/* Response — rendered as markdown */}
                  <div className="max-h-[350px] overflow-y-auto">
                    <PersonaMarkdown content={c.response} size="small" />
                  </div>

                  {/* Auto-judge analysis (shown after auto-judge, before vote) */}
                  {analysis && !match.verdict && (
                    <div className={`mt-3 pt-3 border-t ${darkMode ? 'border-gray-700/40' : 'border-gray-200'}`}>
                      <div className="space-y-1.5">
                        {analysis.strengths?.map((s, i) => (
                          <div key={`s${i}`} className="flex items-start gap-1.5">
                            <span className="text-green-400 text-xs mt-0.5">+</span>
                            <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{s}</span>
                          </div>
                        ))}
                        {analysis.weaknesses?.map((w, i) => (
                          <div key={`w${i}`} className="flex items-start gap-1.5">
                            <span className="text-red-400 text-xs mt-0.5">-</span>
                            <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{w}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Vote button */}
                  {!match.verdict && !autoJudge && (
                    <button
                      onClick={() => handleVote(c.persona_id)}
                      disabled={voting}
                      className="mt-4 w-full py-2 text-sm rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <FiCheck className="w-4 h-4" /> Pick Winner
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Auto-judge summary card */}
          {autoJudge && !match.verdict && (
            <div className={`${sectionClass} border-amber-500/30`}>
              <div className="flex items-center gap-2 mb-3">
                <FiCpu className="w-4 h-4 text-amber-400" />
                <h3 className={`text-sm font-semibold ${darkMode ? 'text-amber-300' : 'text-amber-600'}`}>Auto-Judge Analysis</h3>
              </div>
              <div className="mb-4">
                <PersonaMarkdown content={autoJudge.summary} size="small" />
              </div>
              <div className="flex justify-center gap-3">
                <button
                  onClick={handleAcceptVerdict}
                  disabled={voting}
                  className="px-5 py-2 text-sm rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 disabled:opacity-50 flex items-center gap-2"
                >
                  <FiCheck className="w-4 h-4" /> Accept Verdict
                </button>
                <button
                  onClick={() => setAutoJudge(null)}
                  className={`px-5 py-2 text-sm rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
                >
                  Override — I'll Pick
                </button>
              </div>
            </div>
          )}

          {/* Verdict buttons — shown when no auto-judge active */}
          {!match.verdict && !autoJudge && (
            <div className="flex justify-center gap-3">
              <button
                onClick={handleAutoJudge}
                disabled={judging}
                className="px-4 py-2 text-sm rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 flex items-center gap-1.5"
              >
                <FiCpu className="w-4 h-4" />
                {judging ? 'Judging...' : 'Auto-Judge'}
              </button>
              <button onClick={() => handleVote('tie')} disabled={voting}
                className={`px-4 py-2 text-sm rounded-lg flex items-center gap-1.5 ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} disabled:opacity-50`}>
                <FiMinus className="w-4 h-4" /> Tie
              </button>
              <button onClick={() => handleVote('void')} disabled={voting}
                className={`px-4 py-2 text-sm rounded-lg flex items-center gap-1.5 ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} disabled:opacity-50`}>
                <FiX className="w-4 h-4" /> Both Bad
              </button>
              <button onClick={() => handleVote('skip')} disabled={voting}
                className={`px-4 py-2 text-sm rounded-lg flex items-center gap-1.5 ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} disabled:opacity-50`}>
                <FiSkipForward className="w-4 h-4" /> Skip
              </button>
            </div>
          )}

          {/* Post-match actions */}
          {match.verdict && (
            <div className="flex justify-center gap-3">
              <button
                onClick={handleRerun}
                className={`px-5 py-2 text-sm rounded-lg flex items-center gap-2 ${darkMode ? 'bg-violet-600 hover:bg-violet-500' : 'bg-violet-500 hover:bg-violet-600'} text-white transition-all`}
              >
                <FiRefreshCw className="w-4 h-4" /> Re-run Same Match
              </button>
              <button
                onClick={handleNewMatch}
                className="px-5 py-2 text-sm rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 transition-all"
              >
                New Match
              </button>
            </div>
          )}
        </>
      )}

      {/* Animated Toast */}
      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 animate-[slideUp_0.3s_ease-out]">
          <div className={`flex items-center gap-3 px-6 py-3 rounded-xl shadow-2xl text-white bg-gradient-to-r ${toast.color}`}
            style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}
          >
            <span className="text-xl">{toast.icon}</span>
            <span className="text-sm font-semibold">{toast.message}</span>
          </div>
        </div>
      )}
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translate(-50%, 20px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>
    </div>
  );
};

export default Arena;
